import { Component, OnInit } from '@angular/core';
import { DwComponent, DwComponentType, DwModalService, DwRoutingService, DwSecurityUserService } from '@devwareapps/devware-cap';
import { AppMetaDataLookups, RoSchoolCourseEnrollmentStatsEntity } from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { SchoolReportingFilters } from '../../models/school-reporting-filters.model';
import { SchoolCourseStatEntity } from '../../models/stat-entities/school-course-stat-entity.model';
import { ReportingRepositoryService } from '../../services/reporting-repository.service';
import { SchoolMetricCardInfo } from '../../models/metric-cards/school-metric-card-info.model';
import { SchoolMetricCardConfig } from '../../models/metric-cards/school-metric-card-config.model';
import { SchoolKeyMetricsUtilService } from '../../services/school-key-metrics-util.service';
import { SchoolStatEntity } from '../../models/stat-entities/school-stat-entity.model';
import { combineLatest, Observable, of } from 'rxjs';
import { RiskLevel, StudentAtRiskInfo } from '../../models/ui-models/student-at-risk-info.model';
import { SchoolStatUtilService } from '../../services/school-stat-util.service';
import { StudentAtRiskDetails } from '../../models/ui-models/student-at-risk-details.model';
import { map, mergeMap, tap } from 'rxjs/operators';
import { InstructorStatEntity } from '../../models/stat-entities/instructor-stat-entity.model';
import { ModuleOverviewEntity } from '../../models/stat-entities/module-overview-entity.model';
import { DrillDownDetails, DrillDownType, ReferenceInfo } from '../../models/ui-models/drill-down-details.model';
import { ViewStudentInfo } from '../../models/ui-models/view-student-info.model';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';

type AtRiskFilter = 'all' | 'hidden' | 'high' | 'medium' | 'low';

@DwComponent({
    componentType: DwComponentType.pageComponent,
    key: 'school-reporting-page',
    name: 'School Reporting Page',
    isGlobal: true
})
@Component({
    selector: 'app-reporting-page',
    templateUrl: './reporting-page.component.html',
    styleUrls: ['./reporting-page.component.scss']
})
export class ReportingPageComponent implements OnInit {
    coursesLookupId = AppMetaDataLookups.CourseBySchool;
    studentsLookupId = AppMetaDataLookups.StudentBySchool;
    instructorsLookupId = AppMetaDataLookups.InstructorsBySchool;
    groupsLookupId = AppMetaDataLookups.FlightSchoolGroupBySchool;

    lookupParams: any[] = [];

    filters: SchoolReportingFilters = {};
    dateRangeFilter: string = '30d';

    courseStats: SchoolCourseStatEntity[] = [];
    schoolStats: SchoolStatEntity;
    schoolMetricCards: SchoolMetricCardInfo[] = [];
    atRiskStudents: StudentAtRiskInfo[];
    activeAtRiskFilter: AtRiskFilter = 'all';

    // Mock Data for At-Risk Students
    // Mock Data for At-Risk Students


    atRiskDetails: StudentAtRiskDetails;
    noActivityStudents: RoSchoolCourseEnrollmentStatsEntity[];
    noActivityStudentCount: number = 0;
    noActivtyStudentData: any;
    showingAllNoActivityStudents: boolean = false;
    instructorHasNoStudents: boolean;
    groupHasNoStudents: boolean;
    instructorLeaderboard: InstructorStatEntity[];
    instructorLeaderboardData: InstructorStatEntity[];
    moduleOverviewData: ModuleOverviewEntity[];
    moduleOverview: ModuleOverviewEntity[];
    showDrillDown: boolean;
    drillDownDataChanged: boolean;

    constructor(
        private adminManagerService: AdminManagerService,
        private reportingRepositoryService: ReportingRepositoryService,
        private schoolKeyMetricsUtilService: SchoolKeyMetricsUtilService,
        private schoolStatUtilService: SchoolStatUtilService,
        private dwRoutingService: DwRoutingService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwModalService: DwModalService

    ) { }

    ngOnInit(): void {
        const aviatorUser = this.adminManagerService.getAviatorUser();

        this.lookupParams.push(aviatorUser.FlightSchoolId);

        // Set initial date filter
        this.onDateRangeChange(this.dateRangeFilter);

        this.refreshData();
    }

    updateFilters() {
        this.drillDownReferenceData = null; // Clear cached reference data
        if (this.filters.instructorId || this.filters.groupId) {
            this.updateAdvancedFilters()
                .subscribe(() => {
                    this.refreshData();
                });

            return;
        }

        // These the student filters
        this.filters.instructorStudentIds = [];
        this.filters.groupStudentIds = [];

        // Need to check if instructor or group has changed and load those students to filter by
        this.refreshData();
    }

    backToOverview() {
        this.showDrillDown = false;
    }

    drillDownDetails: DrillDownDetails;

    private drillDownReferenceData: RoSchoolCourseEnrollmentStatsEntity[];

    drillDownClick(drillDownType: DrillDownType, refernceInfo?: ReferenceInfo) {
        this.drillDownDetails = null;
        //this.showDrillDown = true;

        if (drillDownType == DrillDownType.assignmentsCurrent || drillDownType == DrillDownType.assignmentsPastDue) {
            this.drillDownAssignmentsClick(drillDownType);
            return;
        }

        this.getDrillDownReferenceData()
            .subscribe((data) => {
                this.showDrillDown = true;
                this.drillDownDetails = this.schoolStatUtilService.buildDrillDownFilters(drillDownType, refernceInfo, data);
            });
    }

    drillDownAssignmentsClick(drillDownType: DrillDownType) {
        switch (drillDownType) {
            case DrillDownType.assignmentsCurrent:
                this.dwRoutingService.navigateToUrl('/assignments');
                break;
            case DrillDownType.assignmentsPastDue:
                this.dwRoutingService.navigateToUrl('/assignments?tile=PastDue');
                break;
        }
    }


    private getDrillDownReferenceData(): Observable<RoSchoolCourseEnrollmentStatsEntity[]> {
        if (this.drillDownReferenceData && !this.drillDownDataChanged) {
            return of(this.drillDownReferenceData);
        }

        this.drillDownDataChanged = false;

        return this.reportingRepositoryService.getDrillDownReferenceData(this.filters)
            .pipe(mergeMap(data => {

                if (this.drillDownDataChanged) {
                    return this.getDrillDownReferenceData();
                }

                this.drillDownReferenceData = data;

                return of(this.drillDownReferenceData);
            }));
    }

    /**
     * Updates the advanced filters (instructor students and group students) based on the selected
     * instructorId and groupId in the main filters.  We have to load reference data for these, so we only process if selected
     * @returns 
     */
    private updateAdvancedFilters(): Observable<any> {
        const instructorStudentsOb = this.reportingRepositoryService.getInstructorStudents(this.filters.instructorId);
        const groupStudentsOb = this.reportingRepositoryService.getSchoolGroupStudents(this.filters.groupId);

        return combineLatest([instructorStudentsOb, groupStudentsOb])
            .pipe(tap(([instructorStudents, groupStudents]) => {
                this.filters.instructorStudentIds = instructorStudents.map(s => s.StudentId);

                this.filters.groupStudentIds = groupStudents.map(s => s.StudentId);
                this.instructorHasNoStudents = false;
                this.groupHasNoStudents = false;

                if (this.filters.instructorId && this.filters.instructorStudentIds.length === 0) {
                    // No students for this instructor, set to -1 to ensure no results
                    this.filters.instructorStudentIds = [-1];
                    this.instructorHasNoStudents = true;
                }

                if (this.filters.groupId && this.filters.groupStudentIds.length === 0) {
                    // No students for this group, set to -1 to ensure no results
                    this.filters.groupStudentIds = [-1];
                    this.groupHasNoStudents = true;
                }
            }));
    }

    loading = false;

    refreshData() {
        // Note: Loading all at once for simplicity, but could be split into multiple calls if needed (Could use scroll position so we don't load everything until it's needed)
        const courseStatsOb = this.reportingRepositoryService.getCourseEnrollmentStats(this.filters);
        const schoolStatsOb = this.reportingRepositoryService.getSchoolStats(this.filters);
        const noActivtyStudentsOb = this.reportingRepositoryService.getNoActivityStudents(this.filters, 100);
        const instructorLeaderboardOb = this.reportingRepositoryService.getInstructorLeaderboard(this.filters);
        const moduleOverviewOb = this.reportingRepositoryService.getModuleOverviewStats(this.filters);
        this.loading = true; 
 
        combineLatest([courseStatsOb, schoolStatsOb, noActivtyStudentsOb, instructorLeaderboardOb, moduleOverviewOb])
            .subscribe(([courseStats, schoolStats, noActivtyStudentData, instructorLeaderboard, moduleOverview]) => {
                // Set Data for each section
                this.courseStats = courseStats;

                this.schoolStats = schoolStats;

                this.noActivtyStudentData = noActivtyStudentData;
                this.noActivityStudentCount = noActivtyStudentData.PageInfo.TotalRecordCount;

                this.setNoActivityData();

                // Build Metric Cards
                this.schoolMetricCards = this.schoolKeyMetricsUtilService.buildSchoolMetricCardInfos(this.schoolStats);

                this.instructorLeaderboardData = this.schoolStatUtilService.buildInstructorRankings(instructorLeaderboard);

                this.setInstructorLeaderboardData(false);

                this.moduleOverviewData = this.schoolStatUtilService.buildModuleOverview(moduleOverview);

                this.setModuleOverviewData(false);

                this.drillDownDataChanged = true;

                if (this.showDrillDown) {
                    this.drillDownClick(this.drillDownDetails.drillDownType, this.drillDownDetails.referenceInfo)
                }

                // Load At-Risk students later since it depends on course stats
                this.reportingRepositoryService.getAtRiskStudents(this.filters)
                    .subscribe((atRiskStudents) => {
                        // Build At-Risk Student Details (determines risk levels, sorts/filters, etc)
                        this.atRiskDetails = this.schoolStatUtilService.buildAtRiskStudentList(atRiskStudents, courseStats);

                        // Show only high risk to start
                        if (this.atRiskDetails.highRiskStudentCount > 0) {
                            this.setAtRiskStudents(RiskLevel.high);
                        } else {
                            this.setAtRiskStudents('all');
                        }

                        this.loading = false;
                    });
            });
    }

    readonly maxModuleOverviewToShow = 5;
    showingAllModuleOverviews = false;

    setModuleOverviewData(showAll: boolean = false) {
        this.moduleOverview = [...this.moduleOverviewData];

        if (!showAll) {
            if (this.moduleOverview.length > this.maxModuleOverviewToShow) {
                this.moduleOverview.length = this.maxModuleOverviewToShow;
            }
        } else {
            this.showingAllModuleOverviews = true;
        }
    }

    readonly maxInstructorsToShow = 5;
    showingAllInstructors = false;

    setInstructorLeaderboardData(showAll: boolean = false) {
        this.instructorLeaderboard = [...this.instructorLeaderboardData];

        if (!showAll) {
            if (this.instructorLeaderboard.length > this.maxInstructorsToShow) {
                this.instructorLeaderboard.length = this.maxInstructorsToShow;
            }
        } else {
            this.showingAllInstructors = true;
        }
    }

    readonly maxNoActivtyStudentsToShow = 5;

    setNoActivityData(showAll: boolean = false) {

        this.noActivityStudents = [...this.noActivtyStudentData.Data];

        if (!showAll) {
            if (this.noActivityStudents.length > this.maxNoActivtyStudentsToShow) {
                this.noActivityStudents.length = this.maxNoActivtyStudentsToShow;
            }
        } else {
            this.showingAllNoActivityStudents = true;
        }
    }

    maxAtRiskStudentsToShow = 20;

    setAtRiskStudents(riskLevel: string, showHiddenOnly: boolean = false) {
        const normalizedRiskLevel = riskLevel.toLowerCase();

        this.activeAtRiskFilter = this.getActiveAtRiskFilter(normalizedRiskLevel, showHiddenOnly);

        if (normalizedRiskLevel === 'all') {
            if (showHiddenOnly) {
                this.atRiskStudents = this.atRiskDetails.studentsAtRisk.filter(s => s.Hidden);
            } else {
                this.atRiskStudents = this.atRiskDetails.studentsAtRisk.filter(s => !s.Hidden);
            }
        } else {
            this.atRiskStudents = this.atRiskDetails.studentsAtRisk.filter(s => s.Risk && s.Risk.toLowerCase() === normalizedRiskLevel && !s.Hidden);
        }

        if (this.atRiskStudents.length > this.maxAtRiskStudentsToShow) {
            this.atRiskStudents.length = this.maxAtRiskStudentsToShow;
        }
    }

    private getActiveAtRiskFilter(riskLevel: string, showHiddenOnly: boolean): AtRiskFilter {
        if (riskLevel === 'all') {
            return showHiddenOnly ? 'hidden' : 'all';
        }

        switch (riskLevel) {
            case 'high':
            case 'medium':
            case 'low':
                return riskLevel;
            default:
                return 'all';
        }
    }

    viewAtRiskStudentInfo(student: StudentAtRiskInfo) {
        const viewStudentInfo: ViewStudentInfo = {
            studentCourseId: student.StudentCourseId,
            userId: student.UserId
        };

        this.viewStudent(viewStudentInfo);
    }

    viewStudent(student: ViewStudentInfo) {
        let url = `/manage-students/edit/${student.userId}/course-progress/${student.studentCourseId}`;

        if (this.dwSecurityUserService.hasPermission(AppPermissions.flightSchoolCanManagerAllUsers)) {
            // Admins go to Manage Users
            url = `/manage-users/edit/${student.userId}/course-progress/${student.studentCourseId}`;
        }

        this.dwRoutingService.navigateToUrl(url);
    }

    toggleHideStudent(student: StudentAtRiskInfo, hide: boolean) {
        let title = hide ? 'Hide Student from Reporting?' : 'Show Student in Reporting?';
        let message = hide ? 'This will hide this student and course from all reporting views.  Are you sure?' :
            'This will include this student and course in all reporting views.  Are you sure?';

        this.dwModalService.showDialog(title, message)
            .pipe(mergeMap(modalResult => {
                if (modalResult.cancelled) {
                    return of(false);
                }

                return this.reportingRepositoryService.updateHideStudentCourseFromReporting(student.StudentCourseId, hide)
                    .pipe(map((result) => true));
            }))
            .subscribe((refresh) => {
                if (refresh) {
                    this.refreshData();
                }
            });
    }

    onDateRangeChange(value: string): void {
        this.dateRangeFilter = value;
        this.filters.lastActivityAfterDate = this.getDateFromRange(value);
        this.refreshData();
    }

    private getDateFromRange(range: string): Date | undefined {
        if (range === 'all') return undefined;
        const days = parseInt(range, 10); // '30d' -> 30
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date;
    }

    atRiskStudentsOriginal: any[] = [
        {
            initials: 'JW',
            name: 'James Wilson',
            risk: 'high risk',
            riskClass: 'high',
            course: 'Private Pilot Ground School',
            progress: 28,
            pastDue: 5,
            lastActivity: 'Nov 10',
            tags: ['Past Due', 'Low Progress']
        },
        {
            initials: 'MG',
            name: 'Maria Garcia',
            risk: 'high risk',
            riskClass: 'high',
            course: 'Instrument Rating',
            progress: 45,
            pastDue: 3,
            lastActivity: 'Nov 12',
            tags: ['Past Due', 'No Activity']
        },
        {
            initials: 'RC',
            name: 'Robert Chen',
            risk: 'medium risk',
            riskClass: 'medium',
            course: 'Commercial Pilot',
            progress: 52,
            pastDue: 2,
            lastActivity: 'Nov 14',
            tags: ['Past Due']
        }
    ];

    // Mock Data for No Activity Students
    noActivityStudentsOriginal = [
        { initials: 'LA', name: 'Lisa Anderson', course: 'Private Pilot', progress: 42, lastActivity: 'Nov 11' },
        { initials: 'TB', name: 'Tom Bradley', course: 'Instrument', progress: 58, lastActivity: 'Nov 10' },
        { initials: 'NP', name: 'Nina Patel', course: 'Commercial', progress: 35, lastActivity: 'Nov 9' },
        { initials: 'CT', name: 'Chris Taylor', course: 'Multi-Engine', progress: 67, lastActivity: 'Nov 9' },
        { initials: 'AL', name: 'Amy Lee', course: 'CFI', progress: 49, lastActivity: 'Nov 8' }
    ];

    // Mock Data for Instructor Performance
    instructorPerformanceOriginal = [
        { rank: 1, name: 'Captain John Smith', students: 42, completionRate: 89, acsPassRate: 95, avgAssignments: 14.2 },
        { rank: 2, name: 'Lt. Sarah Williams', students: 38, completionRate: 86, acsPassRate: 82, avgAssignments: 12.8 },
        { rank: 3, name: 'CFI Mike Johnson', students: 35, completionRate: 82, acsPassRate: 88, avgAssignments: 11.5 },
        { rank: 4, name: 'Capt. Emily Davis', students: 28, completionRate: 78, acsPassRate: 85, avgAssignments: 9.3 }
    ];

    // Mock Data for Module Overview
    moduleOverviewOriginal = [
        { module: 'Navigation & Chart Reading', course: 'Private Pilot', avgScore: 88, retakeRate: 42, avgTime: '2.3 hrs' },
        { module: 'IFR Approach Procedures', course: 'Instrument', avgScore: 72, retakeRate: 38, avgTime: '3.1 hrs' },
        { module: 'Performance & Weight', course: 'Commercial', avgScore: 74, retakeRate: 35, avgTime: '2.8 hrs' },
        { module: 'Teaching Fundamentals', course: 'CFI', avgScore: 76, retakeRate: 32, avgTime: '2.5 hrs' },
        { module: 'Emergency Procedures', course: 'Private Pilot', avgScore: 78, retakeRate: 28, avgTime: '1.9 hrs' }
    ];
}
